import React, { useState } from 'react';
import styles from './ToolCallCard.module.css';
import { ReactComponent as ToolCallIconSVG } from '../../assets/images/tool-call.svg';

interface ToolCallProps {
  name: string;
  parameters: Record<string, any>;
  result?: any;
}

// Global state manager for expanded states
class ToolCallStateManager {
  private static instance: ToolCallStateManager;
  private expandedStates = new Map<string, boolean>();

  static getInstance(): ToolCallStateManager {
    if (!ToolCallStateManager.instance) {
      ToolCallStateManager.instance = new ToolCallStateManager();
    }
    return ToolCallStateManager.instance;
  }

  getExpandedState(key: string): boolean {
    return this.expandedStates.get(key) || false;
  }

  setExpandedState(key: string, expanded: boolean): void {
    this.expandedStates.set(key, expanded);
  }
}

const ToolCallCard: React.FC<ToolCallProps> = React.memo(({ name, parameters, result }) => {
  // Create a stable key for this tool call to preserve state across re-renders
  const toolKey = `${name}-${JSON.stringify(parameters)}`;
  
  // Get the global state manager
  const stateManager = ToolCallStateManager.getInstance();
  
  const [isExpanded, setIsExpanded] = useState(() => {
    return stateManager.getExpandedState(toolKey);
  });

  // Update the global state when local state changes
  const handleToggle = React.useCallback(() => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    stateManager.setExpandedState(toolKey, newState);
  }, [isExpanded, toolKey, stateManager]);

  const pascalToCamel = (str: string) => {
    if (!str) return str;
    
    // If already camelCase (no underscores and starts with lowercase), return as is
    if (!str.includes('_') && str.charAt(0) === str.charAt(0).toLowerCase()) {
      return str;
    }
    
    // Convert to lowercase and split by underscores
    const words = str.toLowerCase().split('_');
    
    // First word stays lowercase, capitalize first letter of subsequent words
    return words.map((word, index) => 
      index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
    ).join('');
  };

  const toolName = pascalToCamel(parameters['path']?.['name']) || name;

  return (
    <div className={styles.toolCard}>
      <div 
        className={styles.toolHeader} 
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        {/* Tool icon */}
        <ToolCallIconSVG 
          className={styles.toolIcon}
          width="20"
          height="20"
        />
        
        <div className={styles.toolInfo}>
          <span className={styles.toolLabel}>Called tool:</span>
          <span className={styles.toolName} title={toolName}>{toolName}</span>
        </div>
        
        {/* Expand/collapse icon */}
        <svg 
          className={styles.expandIcon} 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none"
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <path 
            d="M6 9l6 6 6-6" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {isExpanded && (
        <div className={styles.toolBody}>
          {Object.entries(parameters).map(([key, value]) => (
            <div key={key} className={styles.paramRow}>
              <div className={styles.paramKey}>{key}:</div>
              <div className={styles.paramValue}>
                {typeof value === 'object' 
                  ? JSON.stringify(value, null, 2) 
                  : String(value)}
              </div>
            </div>
          ))}
          
          {result && (
            <div className={styles.resultSection}>
              <div className={styles.resultLabel}>→ Result:</div>
              <pre className={styles.resultValue}>
                {typeof result === 'object' 
                  ? JSON.stringify(result, null, 2) 
                  : String(result)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.name === nextProps.name &&
    JSON.stringify(prevProps.parameters) === JSON.stringify(nextProps.parameters) &&
    JSON.stringify(prevProps.result) === JSON.stringify(nextProps.result)
  );
});

export default ToolCallCard;

